// Career stats + recent runs aggregations for the profile page.
//
// All reads go through the public `runs` table (RLS allows SELECT to anyone).
// Aggregates are computed client-side because the dataset per user is small
// (typically <500 rows) and we want to keep the migration footprint minimal.

import { getSupabase } from "./client";
import { getUserId } from "./auth";

export interface CareerStats {
  runsPlayed:  number;
  runsWon:     number;
  bestScore:   number;
  bestMapKey:  string | null;
  bestDifficulty: number | null;
  totalScore:  number;
  fastestWinMs: number | null;
}

export interface RunRow {
  id:           number;
  mapKey:       string;
  difficulty:   number;
  outcome:      "win" | "loss";
  wavesCleared: number;
  leaks:        number;
  durationMs:   number;
  score:        number;
  submittedAt:  string;
}

export interface PublicProfile {
  userId:      string;
  displayName: string;
  friendCode:  string;
  createdAt:   string;
  avatarId:    string | null;
}

export async function fetchPublicProfile(userId: string): Promise<PublicProfile | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data, error } = await sb
    .from("profiles")
    .select("user_id, display_name, friend_code, created_at, avatar_id")
    .eq("user_id", userId)
    .maybeSingle();
  if (error || !data) { if (error) console.warn("[profile] public fetch failed", error); return null; }
  return {
    userId:      data.user_id,
    displayName: data.display_name,
    friendCode:  data.friend_code,
    createdAt:   data.created_at,
    avatarId:    data.avatar_id ?? null
  };
}


export async function fetchCareerStats(userId: string): Promise<CareerStats> {
  const empty: CareerStats = {
    runsPlayed: 0, runsWon: 0, bestScore: 0,
    bestMapKey: null, bestDifficulty: null,
    totalScore: 0, fastestWinMs: null
  };
  const sb = getSupabase();
  if (!sb) return empty;
  // Cap at 5,000 rows: career stats are an approximation by design and the
  // RPC for true server-side aggregation is intentionally out of scope.
  // 5k rows ≈ ~30 KB and comfortably covers any realistic player history.
  const { data, error } = await sb
    .from("runs")
    .select("map_key, difficulty, outcome, score, duration_ms")
    .eq("user_id", userId)
    .order("submitted_at", { ascending: false })
    .limit(5000);
  if (error || !data) { if (error) console.warn("[stats] fetch failed", error); return empty; }

  const stats = { ...empty };
  for (const r of data) {
    // Defensive narrowing — if RLS or schema drift returns nulls we'd
    // otherwise NaN-propagate into best/fastest.
    if (typeof r.score !== "number" || typeof r.duration_ms !== "number") continue;
    stats.runsPlayed += 1;
    stats.totalScore += r.score;
    if (r.outcome === "win") {
      stats.runsWon += 1;
      if (stats.fastestWinMs === null || r.duration_ms < stats.fastestWinMs) {
        stats.fastestWinMs = r.duration_ms;
      }
    }
    if (r.score > stats.bestScore) {
      stats.bestScore = r.score;
      stats.bestMapKey = r.map_key;
      stats.bestDifficulty = r.difficulty;
    }
  }
  return stats;
}

export async function fetchRecentRuns(userId: string, limit = 10): Promise<RunRow[]> {
  const sb = getSupabase();
  if (!sb) return [];
  const { data, error } = await sb
    .from("runs")
    .select("id, map_key, difficulty, outcome, waves_cleared, leaks, duration_ms, score, submitted_at")
    .eq("user_id", userId)
    .order("submitted_at", { ascending: false })
    .limit(limit);
  if (error || !data) { if (error) console.warn("[stats] recent failed", error); return []; }
  return data.map((r): RunRow => ({
    id:           r.id,
    mapKey:       r.map_key,
    difficulty:   r.difficulty,
    outcome:      r.outcome,
    wavesCleared: r.waves_cleared,
    leaks:        r.leaks,
    durationMs:   r.duration_ms,
    score:        r.score,
    submittedAt:  r.submitted_at
  }));
}
